import { useContext } from "react";

import classnames from "classnames";
import { Field, useFormikContext } from "formik";
import { notEqualsDeep, isNotEmpty } from "neetocist";
import { FormikEditor } from "neetoeditor";
import { Button } from "neetoui";
import { ActionBlock, Radio } from "neetoui/formik";
import PropTypes from "prop-types";
import { equals } from "ramda";
import { useTranslation } from "react-i18next";

import { EDITOR_ADDONS } from "./constants";
import { EmailFormContext } from "./contexts/EmailForm";
import ReplyToField from "./ReplyToField";
import SendToField from "./SendToField";
import SubjectField from "./SubjectField";
import { normalizeHtmlString } from "./utils";

const EmailForm = ({
  messageVariables = [],
  subjectVariables = [],
  handleCancel,
  handleResetToDefault,
  isLoading,
  replyToFieldProps,
  sendToFieldProps,
  replyToOptions,
  sendToOptions = [],
  isUpdating = false,
  isResetting = false,
  showFields = true,
  className = "",
  editorAddons = EDITOR_ADDONS,
  showResetToDefaultButton,
  editorRef = null,
  defaultValues = {},
}) => {
  const { t } = useTranslation();

  const {
    showSendToField,
    showSendToAsRadio,
    showReplyToField,
    showCcField,
    showBccField,
  } = useContext(EmailFormContext);

  const { setFieldValue, values, resetForm } = useFormikContext();

  const normalizedValues = {
    ...values,
    message: normalizeHtmlString(values.message),
  };

  const normalizedDefaultValues = {
    ...defaultValues,
    message: normalizeHtmlString(defaultValues?.message),
  };

  normalizedDefaultValues.replyTo ||= globalProps.user.email;

  const disabled = equals(normalizedValues, normalizedDefaultValues);

  const sendToFieldOptions = sendToFieldProps?.options || sendToOptions;
  const showSendToRadioField =
    showSendToField && showSendToAsRadio && isNotEmpty(sendToFieldOptions);

  const showSendToTextField = showSendToField && !showSendToRadioField;

  return (
    <div
      data-testid="email-form"
      className={classnames(
        "neeto-notification-email-wrapper__form-container flex flex-1 flex-col items-start gap-2 !pb-0",
        { [className]: className }
      )}
    >
      <div className="w-full grow">
        {showFields && (
          <div className="flex w-full flex-col items-start justify-start gap-5">
            {showSendToRadioField && (
              <Radio
                className="pb-2"
                data-testid="send-to-id"
                label={t("neetoMolecules.emailForm.labels.sendTo")}
                labelProps={{ required: true }}
                name="sendToId"
              >
                {sendToFieldOptions.map(({ label, value }) => (
                  <Radio.Item {...{ label, value }} key={value} />
                ))}
              </Radio>
            )}
            <SendToField
              {...{ showBccField, showCcField, showSendToTextField }}
            />
            {showReplyToField && (
              <div className="neeto-ui-border-gray-200 w-full border-b pb-1">
                <ReplyToField options={replyToOptions} {...replyToFieldProps} />
              </div>
            )}
            <Field name="subject">
              {({ field: { value, onBlur }, meta: { error, touched } }) => (
                <div className="neeto-notification-email-subject-block neeto-ui-border-gray-200 w-full border-b pb-1">
                  <SubjectField
                    {...{ onBlur, subjectVariables }}
                    required
                    error={touched && error ? t(error) : undefined}
                    name="subject"
                    text={value}
                    updateText={text => setFieldValue("subject", text)}
                  />
                </div>
              )}
            </Field>
            <div
              className="neeto-notification-email-message-block w-full"
              data-testid="message-input-container"
            >
              <FormikEditor
                required
                addons={editorAddons}
                data-testid="message-input-field"
                hideSlashCommands={false}
                id="form_message"
                menuType="fixed"
                name="message"
                placeholder={t("neetoMolecules.emailForm.placeholders.message")}
                ref={editorRef}
                variables={messageVariables}
                shouldUpdate={(nextProps, prevProps) =>
                  notEqualsDeep(nextProps.variables, prevProps.variables)
                }
              />
            </div>
          </div>
        )}
      </div>
      <div className="neeto-ui-bg-white neeto-notification-email-wrapper__action-block z-10 flex w-full justify-between py-6 lg:sticky lg:bottom-0">
        <ActionBlock
          cancelButtonProps={{ onClick: handleCancel }}
          isSubmitting={isLoading || isUpdating}
        />
        {showResetToDefaultButton && (
          <div className="neeto-ui-action-block__wrapper">
            <Button
              {...{ disabled }}
              data-testid="reset-to-default-button"
              label={t("neetoMolecules.emailForm.labels.resetToDefault")}
              loading={isResetting}
              style="tertiary"
              onClick={() => {
                resetForm();
                handleResetToDefault();
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

EmailForm.propTypes = {
  /**
   * This is an array of objects used for templating within the message field. Each object in the array has a **label** and a **key**.
   */
  messageVariables: PropTypes.array,
  /**
   * This is an array of objects used for templating within the subject field. Each object in the array has a **label** and a **key**.
   */
  subjectVariables: PropTypes.array,
  /**
   * This function that gets triggered when the 'Cancel' button is clicked.
   */
  handleCancel: PropTypes.func,
  /**
   * This function that gets triggered when the 'Reset to default' button is clicked.
   */
  handleResetToDefault: PropTypes.func,
  /**
   * This Boolean value, when set to true, triggers the display of a loading indicator on the 'Save changes' button."
   */
  isLoading: PropTypes.bool,
  /**
   * This Boolean value, when set to true, triggers the display of a loading indicator on the 'Reset to default' button.
   */
  isResetting: PropTypes.bool,
  /**
   * An array of objects, with each object having **label** and **value** properties. This prop transforms the 'Reply To' field into a dropdown menu with given options. Without this prop, 'Reply To' will be a text input field.
   */
  replyToOptions: PropTypes.array,
  /**
   * An array of objects, with each object having **label** and **value** properties. This prop transforms the 'Send To' field into a radio group with given options. Without this prop, 'Send To' will be a multi email input field.
   */
  sendToOptions: PropTypes.array,
  /**
   * A Boolean flag indicating whether an update operation is currently in progress. When set to `true`, the 'Save changes' button is disabled.
   */
  isUpdating: PropTypes.bool,
  /**
   * To provide additional classnames to the button.
   */
  className: PropTypes.string,
  /**
   * To provide props to the replyToField. Supports options that will
   * be rendered as the reply to options and popover props.
   */
  replyToFieldProps: PropTypes.object,
  /**
   * To provide props to the sendToField. Supports sendTo options.
   */
  sendToFieldProps: PropTypes.object,
  /**
   * This Boolean value, when set to true, shows reset to default button.
   */
  showResetToDefaultButton: PropTypes.bool,
  /**
   * A reference to the editor.
   */
  editorRef: PropTypes.any,
  /**
   * To override the default editor addons
   */
  editorAddons: PropTypes.array,
  /**
   * The default values for the email form.
   */
  defaultValues: PropTypes.object,
};

export default EmailForm;
